import Client, {ClientDTO} from '../../../models/Client';
import {FpAxios} from 'fp-components';
import {SmsApiData} from '@/models/LoginData';
import {Person, PersonDto} from '@/models/Person';
import Deal, {DealDTO} from '@/models/Deal';
import {Utils, wait} from '@/services/utils/BasicUtils';
import {ProductType} from '@/commons/enums/ProductType';
import {DealStatus} from '@/modules/deals/enums/DealStatus';
import {DealStage} from '@/modules/deals/enums/DealStage';
import LoanApplication, {LoanApplicationDTO} from '@/models/LoanApplication';
import {mapForValidation} from '@/modules/multiForm/shared/MultiformModels';
import _ from 'underscore';
import {ExpertState} from '@/models/ExpertState';
import Activity, {ActivityDTO} from '@/models/Activity';
import {Simulation} from '@/models/simulation/Simulation';
import processProxy from '@/env/processProxy';

class ClientUserApi {
  private clientUserAxios = FpAxios.create();
  readonly baseUrl = 'client-auth';
  readonly ekoDealsBaseUrl = processProxy.VUE_APP_USER_DEALS_BASE_URL;

  async sendSms(phone: string): Promise<void> {
    await this.clientUserAxios.post(`${this.baseUrl}/${phone}`);
  }

  async checkSms(clientLoginData: SmsApiData): Promise<string> {
    const response = await this.clientUserAxios.post<{authToken: string, }>(`${this.baseUrl}/${clientLoginData.phone}/${clientLoginData.smsCode}`);
    return response.data.authToken;
  }

  async getUser(): Promise<Client> {
    const response = await this.clientUserAxios.get<ClientDTO>('shared/client');
    return new Client(response.data);
  }

  async getOwner(): Promise<Person> {
    const response = await this.clientUserAxios.get<PersonDto>('shared/owner');
    return new Person(response.data);
  }

  async editClientDataJSON(client: Client): Promise<ClientDTO> {
    client.clientDataJson = mapForValidation(client.clientDataJson!);
    const data = Utils.changeToNull(_.clone(client));
    const response = await this.clientUserAxios.put(`shared/clients/${client.id}/data`, data);
    return response.data;
  }

  async getLoanApplications(): Promise<LoanApplication[]> {
    const response = await this.clientUserAxios.get<LoanApplicationDTO[]>('shared/applications');
    return response.data.map(dto => new LoanApplication(dto));
  }

  async getActivity(): Promise<Activity> {
    const response = await this.clientUserAxios.get<ActivityDTO>('shared/activity');
    return new Activity(response.data);
  }

  async getDeal(): Promise<Deal> {
    const response = await this.clientUserAxios.get<DealDTO>('shared/deal');
    return new Deal(response.data);
  }

  async getEkoUserData(): Promise<ExpertState> {
    const response = await this.clientUserAxios.get(`${this.ekoDealsBaseUrl}/session`);
    return response.data;
  }

  async getSimulation(simulationId: string): Promise<Simulation> {
    const response = await this.clientUserAxios.get<Simulation>(`${processProxy.VUE_APP_SIMULATION_URL}backend/api/simulation/${simulationId}`);
    return response.data;
  }
}

export default new ClientUserApi();
